import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Spin } from 'antd';
import api, { logoutApi } from '../services/api';

const AuthContext = createContext(null);
const SESSION_REVALIDATE_INTERVAL_MS = 60000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async ({ silent = false } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (!silent) setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.data);
    } catch (err) {
      console.error('[fetchMe error]', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    const revalidateSession = () => {
      if (localStorage.getItem('token')) {
        fetchMe({ silent: true });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        revalidateSession();
      }
    };

    const intervalId = window.setInterval(revalidateSession, SESSION_REVALIDATE_INTERVAL_MS);
    window.addEventListener('focus', revalidateSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', revalidateSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchMe]);

  const login = async (token) => {
    localStorage.setItem('token', token);
    await fetchMe();
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/login';
  };

  const updateUser = (updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const isAuthenticated = !!user;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, fetchMe, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
